/**
 * What a widget report says about the visit it came from (TOADO-911, phase 2):
 * the pages the tab showed, the last things the person did there, their setup
 * when they reported, what the site told the widget about itself, and the
 * tab's earlier reports.
 *
 * Collected in memory in the page and sent only with a report, as a gzipped
 * `session` part, then held to these shapes and redacted again on the server
 * (`apps/api/src/sessionTrail.ts`). Nothing typed is ever in it: a field
 * change carries the field's label, never its value.
 */

export const SESSION_TRAIL_LIMITS = {
  pages: 50,
  actions: 100,
  contextKeys: 20,
  contextKeyChars: 40,
  contextValueChars: 200,
  earlierReports: 20,
  nameChars: 80,
  titleChars: 120,
  urlChars: 2000,
  languages: 8,
} as const;

export interface SessionPage {
  /** Epoch ms the tab showed it. */
  at: number;
  url: string;
  title?: string;
  /** How it was reached (TOADO-921). */
  via?: PageVia;
  /** The furthest down it the person scrolled, in tenths (TOADO-923); the page reported from only. */
  depth?: number;
}

/**
 * How a page was reached (TOADO-921): a full load, a reload, back or forward,
 * or a move within a single-page app. A word from this list, never anything
 * the page wrote.
 */
export const PAGE_VIAS = ['load', 'reload', 'back_forward', 'in_app'] as const;
export type PageVia = typeof PAGE_VIAS[number];

/** A way a page was reached, as the server keeps it: one of the list, else none. */
export function pageVia(value: unknown): PageVia | undefined {
  return typeof value === 'string' && (PAGE_VIAS as readonly string[]).includes(value) ? value as PageVia : undefined;
}

export type SessionAction =
  | {
    at: number;
    kind: 'click';
    /** The element's visible or accessible name, cut to 80 characters. */
    name: string;
    /** Its role, or its tag where it has none. */
    role: string;
    /** The part of the page it is in, from a fixed list; never text. */
    within?: TrailArea;
    /** Its state when it was clicked, before the page reacted (TOADO-920). */
    state?: TrailState;
    /** How long the page took to answer it, in ms, when slow, by the browser's own Event Timing (TOADO-924). */
    respondMs?: number;
    /** Three or more clicks of this one control within a second. */
    rage?: true;
    /** Something that looked clickable did nothing for a second: a guess. */
    possiblyDead?: true;
    /** The developer's test hook on it (`data-testid` and the like), for a control its name picks out (TOADO-927). */
    hook?: TestHook;
    /** Where a link goes, as a shape (`/users/:id/edit`, or another site's host), for a link its name picks out (TOADO-928). */
    goes?: string;
    /** Where the control was in the screenshot, as a box in the viewport measured when it was taken, where one press is one action and its name picks it out (TOADO-919). */
    point?: TrailPoint;
  }
  /** A form field changed: its label only, never its value. */
  | { at: number; kind: 'change'; name: string; role: string; within?: TrailArea }
  /** Enter or Escape, on the page or a control its name picks out, never in a field (TOADO-922). */
  | { at: number; kind: 'key'; key: TrailKey; within?: TrailArea; respondMs?: number }
  /** A form was sent: where it is, never what is in it (TOADO-922). */
  | { at: number; kind: 'submit'; within?: TrailArea }
  | { at: number; kind: 'visibility'; state: 'hidden' | 'visible' }
  | { at: number; kind: 'connection'; state: 'offline' | 'online' }
  | { at: number; kind: 'resize'; width: number; height: number; orientation?: 'portrait' | 'landscape' };

export interface SessionSetup {
  deviceType: 'phone' | 'tablet' | 'desktop';
  touch: boolean;
  screen: { width: number; height: number };
  window: { width: number; height: number };
  languages: string[];
  /** Their clock when they reported, as an ISO time with their offset. */
  localTime: string;
  timezone?: string;
  /** Chromium's `navigator.connection`. */
  connection?: { type?: string; effectiveType?: string; downlinkMbps?: number; saveData?: boolean };
  /** GB, Chromium only. */
  deviceMemory?: number;
  hardwareConcurrency?: number;
  forcedColors?: boolean;
  moreContrast?: boolean;
  cookies: boolean;
  storage: boolean;
}

export interface SessionTrail {
  pages: SessionPage[];
  actions: SessionAction[];
  setup?: SessionSetup;
  /** What the site set with `Toado.setContext()`: up to 20 short values. */
  context?: Record<string, string | number | boolean>;
  /** Receipts of this tab's earlier reports, newest first. */
  earlierReports?: string[];
  /** The viewport's size as the capture's screenshot was taken, which the clicks' points are measured in (TOADO-919). */
  shot?: TrailShot;
}

/** The ARIA roles a trail names an element by (TOADO-911); a page can write anything into a role. */
export const TRAIL_ARIA_ROLES: ReadonlySet<string> = new Set([
  'alert', 'alertdialog', 'application', 'article', 'banner', 'button', 'cell', 'checkbox', 'columnheader', 'combobox',
  'complementary', 'contentinfo', 'definition', 'dialog', 'document', 'feed', 'figure', 'form', 'grid', 'gridcell',
  'group', 'heading', 'img', 'link', 'list', 'listbox', 'listitem', 'log', 'main', 'math', 'menu', 'menubar',
  'menuitem', 'menuitemcheckbox', 'menuitemradio', 'navigation', 'none', 'note', 'option', 'presentation',
  'progressbar', 'radio', 'radiogroup', 'region', 'row', 'rowgroup', 'rowheader', 'scrollbar', 'search', 'searchbox',
  'separator', 'slider', 'spinbutton', 'status', 'switch', 'tab', 'table', 'tablist', 'tabpanel', 'term', 'textbox',
  'timer', 'toolbar', 'tooltip', 'tree', 'treegrid', 'treeitem',
]);;

/** The native tags a trail names an element by; anything else is `element`. */
export const TRAIL_NATIVE_TAGS: ReadonlySet<string> = new Set([
  'a', 'abbr', 'address', 'area', 'article', 'aside', 'audio', 'b', 'blockquote', 'br', 'button', 'canvas', 'caption',
  'cite', 'code', 'col', 'colgroup', 'dd', 'details', 'dialog', 'div', 'dl', 'dt', 'em', 'fieldset', 'figcaption',
  'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'i', 'iframe', 'img', 'input', 'label',
  'legend', 'li', 'main', 'mark', 'menu', 'meter', 'nav', 'ol', 'optgroup', 'option', 'output', 'p', 'picture', 'pre',
  'progress', 'q', 's', 'section', 'select', 'small', 'span', 'strong', 'sub', 'summary', 'sup', 'svg', 'table',
  'tbody', 'td', 'textarea', 'tfoot', 'th', 'thead', 'time', 'tr', 'u', 'ul', 'video', 'g', 'path', 'circle', 'rect',
  'use', 'polygon', 'line', 'text',
]);

/**
 * The parts of a page a click or a change is said to be in: "clicked a link
 * in the navigation" (Jason, 22 Oct). A fixed list, read from the page's
 * landmarks and roles, so it says where without reading a word of the page.
 */
export const TRAIL_AREAS = ['navigation', 'header', 'footer', 'sidebar', 'dialog', 'form', 'search', 'menu', 'toolbar', 'table'] as const;
export type TrailArea = typeof TRAIL_AREAS[number];

/** An area the server keeps: one of the list above, else none. */
export function trailArea(value: unknown): TrailArea | undefined {
  return typeof value === 'string' && (TRAIL_AREAS as readonly string[]).includes(value) ? value as TrailArea : undefined;
}

/** A role the server keeps: one of the lists above, else `element` (Codex, round 16). */
export function trailRole(role: unknown): string {
  return typeof role === 'string' && (TRAIL_ARIA_ROLES.has(role) || TRAIL_NATIVE_TAGS.has(role)) ? role : 'element';
}

/** Marks, format and control characters, and fillers: nothing a reader sees as a character. */
const UNSEEN = /[\p{Cc}\p{Cf}\p{M}\p{Zl}\p{Zp}\u115F\u1160\u3164\uFFA0]/gu;

/**
 * How many characters a reader sees in a name: "7" with a zero-width or bidi mark beside it is
 * one, as a keypad key shows it (z.ai, review of TOADO-929).
 */
export function visibleLength(text: string): number {
  return [...seenText(text)].length;
}

/** A name as a reader sees it: without what `visibleLength` does not count, trimmed. */
export function seenText(text: string): string {
  return text.replace(UNSEEN, '').trim();
}

/**
 * Whether a name is a key of an on-screen keyboard or PIN pad, punctuation
 * aside: one letter or digit alone ("7", "7."), a digit as its first or last
 * word ("2 ABC", "Numpad 7", "Digit 7"), a letter after a key's word ("Key
 * A"), or either written onto one ("Key7"). A rule rather than a list of
 * shapes, since a list was always one shape short (z.ai, review of
 * TOADO-929). It takes "Page 2" for a key too, which only blanks a name and
 * starts the quiet: the safe way to be wrong. A letter elsewhere is not a key,
 * or "I agree" would quiet a site for a day. A symbol alone (a close button's
 * ×, a +) is not a key. Used by the recorder to start the quiet, and by every
 * layer to keep no such name.
 */
export function isKeyName(name: string): boolean {
  const words = seenText(name).split(/\s+/).map((word) => word.replace(/[\p{P}\p{S}]/gu, '')).filter(Boolean);
  if (words.length === 0) return false;
  const first = words[0]!;
  const last = words[words.length - 1]!;
  // One letter or digit alone, or two digits (a time picker's "05"); three is a number, not a key.
  if (words.length === 1 && /^(?:[\p{L}\p{N}]|\p{N}{2})$/u.test(first)) return true;
  if (/^\p{N}$/u.test(first) || /^\p{N}$/u.test(last)) return true;
  if (words.length > 1 && /^\p{L}$/u.test(last) && KEY_WORD.test(words[words.length - 2]!)) return true;
  return [first, last].some((word) => KEY_WRITTEN_ON.test(word) || KEY_CODE.test(word));
}

/** The words a page names a key by. */
const KEY_WORD = /^(?:digit|key|letter|num|numpad|number|button)$/i;
/** One of them with a digit written onto it ("Key7", "Numpad7"); a letter would take "Keys" and "Buttons". */
const KEY_WRITTEN_ON = /^(?:digit|key|letter|num|numpad|number|button)\p{N}$/iu;
/** A key as a keyboard event names it ("KeyA", "Digit7", "Numpad7", "NumpadEnter", "F5"), its case exact. */
const KEY_CODE = /^(?:Key[A-Z]|Digit[0-9]|Numpad[0-9A-Z][a-z]*|F(?:[1-9]|1[0-9]|2[0-4]))$/u;

/**
 * A name as the trail keeps it: none for a key, or for anything a reader sees
 * as one character, whatever sent it. The keys of an on-screen keyboard or PIN
 * pad are typed by clicking, and their names would spell what was typed (z.ai,
 * phase 3 round 15; Codex, review of TOADO-929). A key named in words
 * ("Digit seven") is kept, as it always was.
 */
export function keptName(name: string): string {
  return visibleLength(name) === 1 || isKeyName(name) ? '' : maskLongNumbers(name.replace(EMAIL, '[REDACTED]'));
}

/**
 * A run of digits, from a leading + or bracket, and the separators numbers are written with
 * (spaces, dots, commas, dashes, slashes, brackets), read whole: one class repeated, so one scan
 * of a name is linear and a run is never split, which let a long run's digits fall below the
 * count in each part (Codex, round 2). A run holding eight digits or more is a phone or account
 * number a control shows ("Call (415) 555-0123", "Account 1234 - 5678"), masked as an email is
 * (Jason, 24 Oct, when the extension began naming clicks after typing; review round 1 for the
 * separators); a card number is masked before this already. A year, a short order number or a
 * version stays; a full date ("2026-10-24") has eight digits and is masked.
 */
const NUMBER_RUN = /[+(]{0,2}\p{N}[\p{N}\s.,()/-]*/gu;
const DIGIT = /\p{N}/gu;

function maskLongNumbers(name: string): string {
  return name.replace(NUMBER_RUN, (run) => {
    // The run's own trailing separators stay outside the mask, as they are not part of the number.
    const number = run.replace(/[\s.,()/-]+$/u, '');
    return (number.match(DIGIT)?.length ?? 0) >= 8 ? `[REDACTED]${run.slice(number.length)}` : run;
  });
}

/**
 * An email address, in a name a control carries ("Email alice@example.com"): masked wherever a
 * name is kept, in the page and on the server, whole, a quoted local part or an apostrophe
 * included (with its escaped quotes), and with the marker every reader of a name already knows
 * (`isMasked`), so a masked name is never offered as a locator nor taken for an identity (Codex,
 * security sweep, 23 Oct). A domain of one label counts too (`bob@corp`, an intranet's), so long
 * as it holds a letter: `pkg@1.2.3` is a version, not an address (z.ai, security sweep).
 */
const EMAIL = /(?:"(?:[^"\\\r\n]|\\.)+"|[\p{L}\p{N}!#$%&'*+/=?^_{|}~.-]+)@(?=[\p{N}.-]*\p{L})[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)*/gu;

/**
 * A trail's clicks as the worker and the server keep them, whatever was sent
 * (TOADO-929): no element path, which an earlier recorder sent and which on a
 * keypad says which key was pressed, and a rage mark, a control's state
 * (TOADO-920) or a possibly dead click only beside a name a reader can see. The recorder keeps either
 * only for a control its name picks out on the page; all a trail can show of
 * that is that the name is there, so where a name was taken away they go too.
 *
 * A test hook (TOADO-927) and a link's shape (TOADO-928) likewise, each kept
 * only in its own shape. And since a server cannot see the page, clicks of one
 * role and name on one page that disagree on either (`key-1`, `key-2`; or two
 * shapes) lose it, all of them: only controls sharing a name, which is what a
 * keypad sends, can disagree, and the recorder would have kept neither. Pages
 * are told apart by the page records' times when given; without them the
 * whole trail is one page.
 *
 * A point (TOADO-919) the same way: in its shape, wholly inside the `shot`
 * it was measured in (none without one), beside a name, and lost by every
 * click of one role and name on one page unless all of them carry the very
 * same box. The recorder measures every click of a control at one moment, so
 * they always agree; boxes that differ at all are a keypad's keys or an
 * uploader's code (Codex, review of TOADO-919), and are held to the shot
 * before they are grouped, so the edge cannot pick some of a group out.
 */
export function keptClicks(actions: SessionAction[], pages: ReadonlyArray<{ at: number }> = [], shot?: TrailShot): SessionAction[] {
  const kept = actions.map((action): SessionAction => {
    if (action.kind !== 'click') return action;
    const { selector: _selector, rage, state, possiblyDead, hook: rawHook, goes: rawGoes, point: rawPoint, ...rest } = action as typeof action & { selector?: unknown };
    // A masked name tells keys apart no better than none, so it carries nothing that would.
    const name = String(action.name ?? '');
    const { respondMs: took, ...unnamed } = rest as typeof rest & { respondMs?: number };
    if (visibleLength(name) === 0 || isMasked(name)) return unnamed;
    const hook = testHook(rawHook);
    const goes = trailLinkShape(rawGoes);
    const point = trailPoint(rawPoint);
    const inShot = !!point && !!shot && point.box.x + point.box.width <= shot.width && point.box.y + point.box.height <= shot.height;
    return {
      ...unnamed, ...(state !== undefined ? { state } : {}), ...(rage === true ? { rage } : {}), ...(possiblyDead === true ? { possiblyDead } : {}),
      ...(took !== undefined ? { respondMs: took } : {}), ...(hook ? { hook } : {}), ...(goes ? { goes } : {}), ...(point && inShot ? { point } : {}),
    };
  });
  // Every named click is in its group, those carrying nothing included: a click without a hook is
  // a value in the group as much as one with, or a pattern of with and without would spell what
  // a hostile uploader chose (Codex, review of TOADO-927). A group keeps a field only where every
  // one of its clicks carries it, and carries the same.
  const starts = pages.map((page) => page.at).filter((at) => Number.isFinite(at)).sort((a, b) => a - b);
  const pageOf = (at: number) => starts.filter((start) => start <= at).length;
  type Group = { hooks: Set<string>; shapes: Set<string>; boxes: Set<string> };
  const seen = new Map<string, Group>();
  const named = (action: SessionAction): action is Extract<SessionAction, { kind: 'click' }> => action.kind === 'click' && typeof action.name === 'string' && visibleLength(action.name) > 0;
  const groupOf = (action: Extract<SessionAction, { kind: 'click' }>) => `${pageOf(action.at)}\u0000${action.role}\u0000${seenText(action.name).toLowerCase()}`;
  for (const action of kept) {
    if (!named(action)) continue;
    const group = seen.get(groupOf(action)) ?? { hooks: new Set<string>(), shapes: new Set<string>(), boxes: new Set<string>() };
    group.hooks.add(action.hook ? `${action.hook.attr}=${action.hook.value}` : '');
    group.shapes.add(action.goes ?? '');
    const box = action.point?.box;
    group.boxes.add(box ? `${box.x},${box.y},${box.width},${box.height}` : '');
    seen.set(groupOf(action), group);
  }
  return kept.map((action) => {
    if (!named(action) || (!action.hook && !action.goes && !action.point)) return action;
    const group = seen.get(groupOf(action))!;
    const { hook, goes, point, ...rest } = action;
    return {
      ...rest, ...(hook && group.hooks.size === 1 ? { hook } : {}), ...(goes && group.shapes.size === 1 ? { goes } : {}),
      ...(point && group.boxes.size === 1 ? { point } : {}),
    };
  });
}

/**
 * A control's state when it was clicked, before the page reacted to the click
 * (TOADO-920): yes or no, never what the person picked. Not checked, and not
 * an option's or a field's selection: those are the answer. A tab's selection
 * is where the person was, so it is kept.
 */
export interface TrailState {
  disabled?: true;
  expanded?: boolean;
  pressed?: boolean | 'mixed';
  readOnly?: true;
  /** A tab's only. */
  selected?: boolean;
}

/** A state as the server keeps it: each key only in its shape, or none at all. */
export function trailState(value: unknown): TrailState | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const state: TrailState = {};
  if (raw.disabled === true) state.disabled = true;
  if (typeof raw.expanded === 'boolean') state.expanded = raw.expanded;
  if (typeof raw.pressed === 'boolean' || raw.pressed === 'mixed') state.pressed = raw.pressed;
  if (raw.readOnly === true) state.readOnly = true;
  if (typeof raw.selected === 'boolean') state.selected = raw.selected;
  return Object.keys(state).length > 0 ? state : undefined;
}

/**
 * The keys a trail records (TOADO-922): Enter and Escape alone. Tab, the
 * arrows and the paging keys move focus or change a value, and in a keypad or
 * a rating their sequence spells what was entered however the keys are named
 * (Codex, review of TOADO-922), so none of them is kept.
 */
export const TRAIL_KEYS = ['Enter', 'Escape'] as const;
export type TrailKey = typeof TRAIL_KEYS[number];

/** A key the server keeps: one of the list, else none. */
export function trailKey(value: unknown): TrailKey | undefined {
  return typeof value === 'string' && (TRAIL_KEYS as readonly string[]).includes(value) ? value as TrailKey : undefined;
}

/**
 * How far down the page the bottom of the window was, as a share of the
 * page rounded to tens (0, 10, ... 100) (TOADO-923). Rounded so it says where
 * in the page, never which pixel.
 */
export function scrollDepth(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 100 && value % 10 === 0 ? value : undefined;
}

/**
 * What the redactors put in place of what they take out (`REDACTED` in @vd/redaction, which a
 * session-trail test holds equal). A name holding it no longer tells controls apart, so nothing
 * that would is kept beside it, and no locator is built from it (TOADO-929).
 */
export const REDACTED_MARK = '[REDACTED]';

/**
 * Whether the redactors took something out of a text: the mark as it is, or as a URL's encoding
 * writes it (`%5BREDACTED%5D`), since a name can be an address the URL redactor masked (Codex,
 * review of TOADO-929). One test for every layer.
 */
export function isMasked(text: string): boolean {
  return /\[REDACTED\]|%5BREDACTED%5D/i.test(text);
}

/** The slowest and fastest answers an action keeps (TOADO-924): faster is not slow, and past a minute is no answer. */
export const SLOW_RESPONSE_MS = 200;
export const MAX_RESPONSE_MS = 60_000;

/** How long an action took to answer, as the server keeps it: a whole number of ms in range, else none. */
export function respondMs(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= SLOW_RESPONSE_MS && value <= MAX_RESPONSE_MS ? value : undefined;
}

/** The attributes a developer puts on an element for its tests, in the order they are read (TOADO-927). */
export const TEST_HOOK_ATTRS = ['data-testid', 'data-test-id', 'data-test', 'data-cy', 'data-qa'] as const;
export type TestHookAttr = typeof TEST_HOOK_ATTRS[number];

/** A developer's test hook on a clicked control: which attribute, and its value (TOADO-927). */
export interface TestHook { attr: TestHookAttr; value: string }

/**
 * A test hook as the trail keeps one (TOADO-927): one of the attributes, and a
 * value made of at most six parts joined by `-`, `_`, `.` or `:`, each a word
 * of up to 20 letters or a number of one or two digits, with a word among
 * them, 3 to 64 characters in all, exactly as written (no spaces around it,
 * or a locator would not find it), never a key's name; else none. So an id, a
 * UUID, an account number, or a token with a digit in each part longer than
 * two, is never a hook (Codex, review of TOADO-927). A token made of letters
 * alone in words of up to 20 can still pass (`glpat-abcdefghijklmnopqrst`,
 * z.ai extension security review, 24 Oct); a hook is a site's own test
 * attribute on the clicked control, so that is a site writing a token into
 * one, and it reaches only that site's own report. The recorder keeps one only for the
 * clicked control's own hook, where its name picks it out (as a rage mark),
 * and not when it looks like one of a list (`row-jane` beside `row-bob`).
 */
export function testHook(value: unknown): TestHook | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { attr, value: text } = value as { attr?: unknown; value?: unknown };
  if (typeof attr !== 'string' || !(TEST_HOOK_ATTRS as readonly string[]).includes(attr)) return undefined;
  if (typeof text !== 'string' || text.length < 3 || text.length > 64) return undefined;
  const parts = hookParts(text);
  if (!parts || !parts.some((part) => /^[A-Za-z]/.test(part)) || isKeyName(parts.join(' '))) return undefined;
  return { attr: attr as TestHookAttr, value: text };
}

/** A hook's parts, or none when it is not made of words and small numbers joined by separators. */
export function hookParts(text: string): string[] | undefined {
  if (!/^[A-Za-z0-9]+(?:[-_.:][A-Za-z0-9]+)*$/.test(text)) return undefined;
  const parts = text.split(/[-_.:]/);
  // A part made only of capitals, four or more, is a code (`TOA-ACDEFGHJ`), not a developer's word; a capital run inside a
  // mixed part (`loadJSON`) is a word's (z.ai, review of TOADO-927).
  return parts.length <= 6 && parts.every((part) => (/^[A-Za-z]{1,20}$/.test(part) && !/^[A-Z]{4,}$/.test(part)) || /^\d{1,2}$/.test(part)) ? parts : undefined;
}

/**
 * Where a clicked control was in the screenshot (TOADO-919): its box, in the
 * viewport's CSS pixels, measured as the capture's screenshot was taken. Never
 * where in the control the press landed: on one control drawn as a keypad,
 * the press's place within it spells what was entered (Codex, review of
 * TOADO-919). Measured at the screenshot, not at the click, so a fixed, sticky
 * or scrolled control is where the picture shows it.
 */
export interface TrailPoint { box: { x: number; y: number; width: number; height: number } }

/** The size of the viewport a trail's points were measured in, the screenshot's (TOADO-919). */
export interface TrailShot { width: number; height: number }

/** The largest viewport side, and so the largest box coordinate and side, a point keeps, in pixels. */
const SHOT_MAX = 20_000;

/**
 * A point as the trail keeps one (TOADO-919): a box of whole pixels inside a
 * viewport, at least a pixel each way; else none. The recorder measures one
 * only for a click where one press is one action and the control's name picks
 * it out (as a rage mark), so a keypad's keys, which share a name or are named
 * as keys, never give one, and one control pressed in many places gives one box.
 */
export function trailPoint(value: unknown): TrailPoint | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { box } = value as { box?: unknown };
  if (!box || typeof box !== 'object') return undefined;
  const b = box as { x?: unknown; y?: unknown; width?: unknown; height?: unknown };
  const whole = (n: unknown, min: number): n is number => typeof n === 'number' && Number.isInteger(n) && n >= min && n <= SHOT_MAX;
  if (!whole(b.x, 0) || !whole(b.y, 0) || !whole(b.width, 1) || !whole(b.height, 1)) return undefined;
  return { box: { x: b.x, y: b.y, width: b.width, height: b.height } };
}

/** A shot's size as the trail keeps one: whole pixels, 1 to SHOT_MAX each way; else none (TOADO-919). */
export function trailShot(value: unknown): TrailShot | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { width, height } = value as { width?: unknown; height?: unknown };
  const side = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= SHOT_MAX;
  return side(width) && side(height) ? { width, height } : undefined;
}

/** The most path segments a link's shape keeps (TOADO-928). */
export const LINK_SHAPE_SEGMENTS = 8;

/**
 * Where a link goes, as a shape (TOADO-928): on the page's own site, its path
 * as the URL parser writes it, given to `redact` (the recorder passes the
 * redaction package's), with every segment the redactor changed or that is
 * not a plain word written `:id`; on another site, its registrable domain
 * alone, never a subdomain, which can hold an account or a token, and none if
 * the redactor would change it (Codex, review of TOADO-928). Never decoded: an
 * escaped segment (`%67lpat-…`, a token once decoded) holds a `%`, so it is no
 * word and reads `:id` whatever it hides. Never a query or a fragment. None
 * for anything but an http(s) link. Links to `/pin/1` and `/pin/2` both read
 * `/pin/:id`.
 */
export function linkShape(href: string, pageHref: string, redact: (text: string) => string = (text) => text): string | undefined {
  let url: URL;
  let page: URL;
  try {
    page = new URL(pageHref);
    url = new URL(href, page);
  } catch {
    return undefined;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
  if (url.origin !== page.origin) {
    const domain = registrableDomain(url.hostname);
    return domain && redact(domain) === domain ? domain : undefined;
  }
  // A link to the page the person is on, a fragment or an empty href, goes nowhere new.
  if (url.pathname === page.pathname && url.search === page.search) return undefined;
  const raw = url.pathname.split('/').filter(Boolean).slice(0, LINK_SHAPE_SEGMENTS);
  const redacted = redact(`/${raw.join('/')}`).split('/').slice(1);
  const segments = raw.map((segment, index) => (redacted.length === raw.length && redacted[index] === segment && isLinkWord(segment) ? segment : ':id'));
  return `/${segments.join('/')}`;
}

/**
 * A path segment a link's shape keeps as it is: a plain word, 3 to 32
 * characters, of up to four parts of at most 14 lower-case letters joined by
 * `-` or `_`. A token, letters only or not, is longer than any part allows
 * (Codex, review of TOADO-928: `glpat-abcdefghijklmnopqrst`), and a code in
 * capitals (`TOA-ACDEFGJK`) is no word at all (z.ai, review of TOADO-928).
 */
function isLinkWord(word: string): boolean {
  return word.length >= 3 && word.length <= 32 && /^[a-z]{1,14}(?:[-_][a-z]{1,14}){0,3}$/.test(word);
}

/** The second-level names a country's domains are sold under (`co.uk`, `com.au`): a host ending in one has no site this can name. */
const SECOND_LEVEL = /^(?:co|com|org|net|gov|edu|ac|or|ne|go|gob|nic)$/;

/**
 * A host's site, as its last two labels (`docs.example.com` reads
 * `example.com`), or none: for an address, a single label, anything that is
 * not a host name, and a host whose last two labels look like a country's
 * second level (`co.uk`, `com.au`), since without the public suffix list the
 * label before them may be a site's name or a subdomain of one
 * (`jane-smith.org.de`), and being wrong would keep an account's name (Codex,
 * review of TOADO-928). So a guess never keeps more than two labels.
 */
export function registrableDomain(host: string): string | undefined {
  const labels = host.toLowerCase().split('.');
  if (labels.length < 2 || labels.some((label) => !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label))) return undefined;
  const tld = labels[labels.length - 1]!;
  const second = labels[labels.length - 2]!;
  if (!/^[a-z]{2,24}$/.test(tld) || (tld.length === 2 && SECOND_LEVEL.test(second)) || /^\d+$/.test(second)) return undefined;
  return `${second}.${tld}`;
}

/** A link's shape as the server keeps one (TOADO-928): a path of plain words and `:id`, or a registrable domain; else none. */
export function trailLinkShape(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 300) return undefined;
  if (value.startsWith('/')) {
    const segments = value === '/' ? [] : value.slice(1).split('/');
    return segments.length <= LINK_SHAPE_SEGMENTS && segments.every((segment) => segment === ':id' || isLinkWord(segment)) ? value : undefined;
  }
  return registrableDomain(value) === value ? value : undefined;
}

/** A receipt as the server mints them: TOA- and eight of its alphabet (Codex, round 16). */
export const RECEIPT_PATTERN = /^TOA-[ACDEFGHJKLMNPQRTUVWXY3479]{8}$/;

/** The values the Network Information API gives, the only ones kept. */
export const CONNECTION_TYPES: ReadonlySet<string> = new Set(['bluetooth', 'cellular', 'ethernet', 'mixed', 'none', 'other', 'unknown', 'wifi', 'wimax']);
export const EFFECTIVE_CONNECTION_TYPES: ReadonlySet<string> = new Set(['slow-2g', '2g', '3g', '4g']);

/** Whether a time zone is one the runtime knows, as Intl reads it. */
export function isTimeZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || zone.length === 0 || zone.length > 64) return false;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions().timeZone.length > 0;
  } catch {
    return false;
  }
}

/**
 * The most a widget sends as the session part, as JSON in UTF-8: below the
 * server's bound on the decompressed part (MAX_SESSION_TRAIL_BYTES, 256 KB),
 * so a full trail of long text never fails the whole report (Codex, round 14).
 */
export const SESSION_TRAIL_UPLOAD_BYTES = 240 * 1024;

/**
 * The trail made to fit `maxBytes` as JSON in UTF-8, whole entries only:
 * the oldest actions go first, then the oldest pages, then the site's
 * context and the earlier receipts; null when even the rest is too big.
 * Never cuts a string, so nothing redacted whole is shortened.
 */
export function fitSessionTrail(trail: SessionTrail, maxBytes: number = SESSION_TRAIL_UPLOAD_BYTES): SessionTrail | null {
  const bytes = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).length;
  let total = bytes(trail);
  if (total <= maxBytes) return trail;
  const actions = [...trail.actions];
  const pages = [...trail.pages];
  // Each entry's own share, and the comma before the next one while another remains (Codex, round 15).
  const actionBytes = actions.map((action) => bytes(action));
  const pageBytes = pages.map((page) => bytes(page));
  while (total > maxBytes && actions.length > 0) { actions.shift(); total -= actionBytes.shift()! + (actions.length > 0 ? 1 : 0); }
  while (total > maxBytes && pages.length > 0) { pages.shift(); total -= pageBytes.shift()! + (pages.length > 0 ? 1 : 0); }
  const fitted: SessionTrail = { ...trail, pages, actions };
  if (bytes(fitted) <= maxBytes) return fitted;
  const { context: _context, earlierReports: _earlier, ...rest } = fitted;
  return bytes(rest) <= maxBytes ? rest : null;
}

/**
 * An address no longer than `max`, never cut: the whole address when it
 * fits, else its origin and path, else its origin alone, else nothing. A cut
 * could split a key in the query or path where its shortened form would no
 * longer look like one to a redactor (Codex, round 12). Both the widget and
 * the server bound addresses this way, the server after redacting the whole.
 */
export function boundUrl(href: string, max: number): string {
  if (href.length <= max) return href;
  try {
    const url = new URL(href);
    const path = `${url.origin}${url.pathname}`;
    if (path.length <= max) return path;
    return url.origin.length <= max ? url.origin : '';
  } catch {
    return '';
  }
}

/**
 * Text cut to `max` characters at a word: a word the cut would split is left
 * out whole, so a key or token is never kept in part where its shortened form
 * would no longer look like one to a redactor (Codex, round 8). Text with no
 * space before the cut, one long token, is left out entirely. Both the widget
 * and the server cut this way, after redacting the whole text.
 */
export function cutWhole(text: string, max: number): string {
  if (text.length <= max) return text;
  // Room for the ellipsis; the cut is at `limit` when a space follows it, else at the last space before it.
  const limit = max - 1;
  const at = /\s/.test(text[limit]!) ? limit : text.lastIndexOf(' ', limit - 1);
  const kept = at > 0 ? text.slice(0, at).trimEnd() : '';
  return kept ? `${kept}…` : '';
}

/**
 * A `setContext` key whose value is a secret whatever it looks like: the
 * shared deny sets cover tokens and passwords, and these cover what a site
 * might hold about a person's sign-in or payment besides (Codex, round 2).
 * Both the widget and the server take its value whole.
 */
export function isSensitiveContextKey(key: string): boolean {
  return /(^|[^a-z])(pin|passcode|passphrase|otp|totp|mfa|2fa|cvv|cvc|csc|ssn|sin|tin|iban|card|secret|token|password|passwd|pwd|auth|session|cookie|apikey|api_key)([^a-z]|$)/i.test(key.replace(/([a-z])([A-Z])/g, '$1_$2'));
}
